import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MembershipProvider, useMembership } from "@/context/MembershipContext";
import OnboardingGate from "@/components/OnboardingGate";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ReferralCapture from "@/pages/ReferralCapture";
import AppLayout from "@/components/AppLayout";
import CoachOverview from "@/pages/coach/CoachOverview";
import CoachClients from "@/pages/coach/CoachClients";
import CoachWeeklyReport from "@/pages/coach/CoachWeeklyReport";
import CoachSundayCheckins from "@/pages/coach/CoachSundayCheckins";
import CoachClientDetail from "@/pages/coach/CoachClientDetail";
import CoachClientPlan from "@/pages/coach/CoachClientPlan";
import CoachClientMeals from "@/pages/coach/CoachClientMeals";
import CoachMeals from "@/pages/coach/CoachMeals";
import CoachPrograms from "@/pages/coach/CoachPrograms";
import CoachWaitlistPage from "@/pages/coach/CoachWaitlistPage";
import CoachAILog from "@/pages/coach/CoachAILog";
import CoachMotivationWall from "@/pages/coach/CoachMotivationWall";
import CoachRevenue from "@/pages/coach/CoachRevenue";
import WaitlistPage from "@/pages/WaitlistPage";
import ClientToday from "@/pages/client/ClientToday";
import ClientProgram from "@/pages/client/ClientProgram";
import ClientProgress from "@/pages/client/ClientProgress";
import ClientLogs from "@/pages/client/ClientLogs";
import ClientMessages from "@/pages/client/ClientMessages";
import OnboardingPage from "@/pages/client/OnboardingPage";
import StatsPage from "@/pages/client/StatsPage";
import SundayCheckin from "@/pages/client/SundayCheckin";
import SubscribePage from "@/pages/client/SubscribePage";
import SubscribeSuccessPage from "@/pages/client/SubscribeSuccessPage";
import ReferralsPage from "@/pages/client/ReferralsPage";
import ClientMealsPage from "@/pages/client/ClientMealsPage";
import ClientAccountPage from "@/pages/client/ClientAccountPage";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import JoinPage from "@/pages/funnel/JoinPage";
import JoinUpgradePage from "@/pages/funnel/JoinUpgradePage";
import JoinBookCallPage from "@/pages/funnel/JoinBookCallPage";
import JoinWelcomePage from "@/pages/funnel/JoinWelcomePage";

function Loading() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="id-label text-zinc-500 id-pulse">— Loading —</div>
    </div>
  );
}

function ProtectedRoute({ children, role }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "coach" ? "/coach" : "/app"} replace />;
  }
  return children;
}

// For clients: blocks app routes if membership is inactive (except subscribe pages)
function ClientPaywall({ children }) {
  const { user } = useAuth();
  const { membership } = useMembership();
  const location = useLocation();
  if (user === null || (user && !membership)) return <Loading />;
  // Coach role bypass (defensive)
  if (user?.role === "coach") return children;
  if (membership && !membership.active) {
    const onSubscribe = location.pathname.startsWith("/app/subscribe");
    if (!onSubscribe) return <Navigate to="/app/subscribe" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={user.role === "coach" ? "/coach" : "/app"} replace />;
  return children;
}

function RootRoute() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={user.role === "coach" ? "/coach" : "/app"} replace />;
  // Public launch: root traffic hits the marketing landing page. The /waitlist
  // route is kept live so old links keep working, but nothing redirects there.
  return <LandingPage />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MembershipProvider>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/waitlist" element={<WaitlistPage />} />
              <Route path="/r/:code" element={<ReferralCapture />} />
              {/* Paid-ads funnel — public, standalone (no shared header/footer) */}
              <Route path="/join" element={<JoinPage />} />
              <Route path="/join/upgrade" element={<JoinUpgradePage />} />
              <Route path="/join/book-call" element={<JoinBookCallPage />} />
              <Route path="/join/welcome" element={<JoinWelcomePage />} />
              {/* Backwards-compat: keep the old upgrade-2 URL routing to welcome
                  in case anything (email links, saved bookmarks) still points there. */}
              <Route path="/join/upgrade-2" element={<JoinWelcomePage />} />
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />

              <Route path="/coach" element={<ProtectedRoute role="coach"><AppLayout><CoachOverview /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/weekly-report" element={<ProtectedRoute role="coach"><AppLayout><CoachWeeklyReport /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/sunday-checkins" element={<ProtectedRoute role="coach"><AppLayout><CoachSundayCheckins /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/clients" element={<ProtectedRoute role="coach"><AppLayout><CoachClients /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/clients/:id" element={<ProtectedRoute role="coach"><AppLayout><CoachClientDetail /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/clients/:id/plan" element={<ProtectedRoute role="coach"><AppLayout><CoachClientPlan /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/clients/:clientId/meals" element={<ProtectedRoute role="coach"><AppLayout><CoachClientMeals /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/meals" element={<ProtectedRoute role="coach"><AppLayout><CoachMeals /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/programs" element={<ProtectedRoute role="coach"><AppLayout><CoachPrograms /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/waitlist" element={<ProtectedRoute role="coach"><AppLayout><CoachWaitlistPage /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/ai-log" element={<ProtectedRoute role="coach"><AppLayout><CoachAILog /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/motivation-wall" element={<ProtectedRoute role="coach"><AppLayout><CoachMotivationWall /></AppLayout></ProtectedRoute>} />
              <Route path="/coach/revenue" element={<ProtectedRoute role="coach"><AppLayout><CoachRevenue /></AppLayout></ProtectedRoute>} />

              {/* Subscribe is reachable without active membership */}
              <Route path="/app/subscribe" element={<ProtectedRoute role="client"><SubscribePage /></ProtectedRoute>} />
              <Route path="/app/subscribe/success" element={<ProtectedRoute role="client"><SubscribeSuccessPage /></ProtectedRoute>} />
              {/* Onboarding — reachable WITHOUT paywall so new clients fill it in first */}
              <Route path="/app/onboarding" element={<ProtectedRoute role="client"><OnboardingPage /></ProtectedRoute>} />

              {/* Gated client routes — onboarding before paywall */}
              <Route path="/app" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><ClientToday /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/stats" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><StatsPage /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/sunday-checkin" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><SundayCheckin /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/program" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><ClientProgram /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/progress" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><ClientProgress /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/logs" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><ClientLogs /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/messages" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><ClientMessages /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/refer" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><ReferralsPage /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/meals" element={<ProtectedRoute role="client"><OnboardingGate><ClientPaywall><AppLayout><ClientMealsPage /></AppLayout></ClientPaywall></OnboardingGate></ProtectedRoute>} />
              <Route path="/app/account" element={<ProtectedRoute role="client"><AppLayout><ClientAccountPage /></AppLayout></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <PWAInstallBanner />
          </MembershipProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
